import _ from 'lodash';
import type { Image } from 'velite';
import config from '#/config';

export type MetaTagProps = React.JSX.IntrinsicElements['meta'];

export type LinkTagProps = React.JSX.IntrinsicElements['link'];

export type ScriptTagProps = React.JSX.IntrinsicElements['script'];

export interface SEOPayload {
  title: string;
  description?: string;
  keywords?: string | Array<string | undefined | null>;
  image?: string | Image;
  url?: string;
}

/**
 * Generates meta tags for SEO
 *
 * @param payload.title The title of the page
 * @param payload.description The description of the page
 * @param payload.keywords The keywords of the page
 * @param payload.image The image of the page
 * @param payload.url The URL of the page
 * @returns An array of meta tags
 */
export function seo({ title, description, keywords, image, url }: SEOPayload): MetaTagProps[] {
  const imgSrc = `${config.domain}${(typeof image === 'string' ? image : image?.src) ?? '/social.png'}`;
  const tags = [
    { title },
    { name: 'description', content: description },
    {
      name: 'keywords',
      content: Array.isArray(keywords) ? _.compact(keywords).join(',') : keywords,
    },
    { name: 'twitter:title', content: title },
    { name: 'twitter:description', content: description },
    { name: 'twitter:creator', content: '@joggrhq' },
    { name: 'twitter:site', content: '@joggrhq' },
    { property: 'og:type', content: 'website' },
    { property: 'og:title', content: title },
    { property: 'og:description', content: description },
    { property: 'og:image', content: imgSrc },
    { name: 'twitter:image', content: imgSrc },
    { name: 'twitter:card', content: imgSrc },
  ] satisfies MetaTagProps[];

  // If a URL is provided, add it to the meta tags otherwise ignore it as we will rely on the
  // __root route to provide the url
  if (url) {
    tags.push({
      property: 'og:url',
      content: `${config.domain}${url}`,
    });
  }

  return tags;
}

/**
 * Get the canonical link tag
 *
 * @param pathname The pathname of the page
 */
export function canonicalLinkTag(pathname: string): LinkTagProps {
  return linkTag({ rel: 'canonical', href: `${config.domain}${pathname}` });
}

/**
 * Create a link tag configuration
 *
 * @param config A link tag configuration
 * @returns A link tag configuration
 */
export function linkTag(config: LinkTagProps): LinkTagProps {
  return config;
}

/**
 * Create a meta tag configuration
 *
 * @param config A meta tag configuration
 * @returns A meta tag configuration
 */
export function metaTag(config: MetaTagProps): MetaTagProps {
  return config;
}

/**
 * Create a script tag configuration
 *
 * @param config A script tag configuration
 * @returns A script tag configuration
 */
export function scriptTag(config: ScriptTagProps) {
  return config;
}
